import React from "react";
import cx from "classnames";
import { css } from "@linaria/core";
import Checkbox from "@ui/Checkbox";
import LinkMemo from "@ui/Link";
import Text from "@ui/Text";
import { usePost } from "../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../../i18n";
import { Hr } from "./DesignComponents";
import SelectionRow from "./SelectionRow";
import ModalForm from "@ui/Modal/Form";
import AnalysesSelectionSummary from "./AnalysesSelectionSummary";
import { DurationInput } from "./DurationInput";
import useActivationRangeDryRun from "../../../../api/useActivationRangeDryRun";
import { InformationLine } from "@ui/IconV2/react";
import { colors, spacings, fontSizes } from "@ui/Theme";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import Tooltip from "@ui/TooltipV2";
import { toPath } from "../../../../router/helpers/path";

interface Props {
  closeModal: () => void;
  from: Date;
  to: Date;
  projectId: string;
  activeAnalysesCount: number;
  dataPointsCount: number;
}

const ActivateAnalyseModalForm = ({
  closeModal,
  from,
  to,
  projectId,
  activeAnalysesCount,
  dataPointsCount,
}: Props) => {
  const activateRange = usePost("/projects/:projectId(uuid)/analyses/activation/range");

  const [bypassDataFilter, setBypassDataFilter] = React.useState(false);
  const [targetAnalysisIntervalInS, setTargetAnalysisIntervalInS] = React.useState(0);
  const { addToast } = useToasts();
  const timeWindowInMS = Math.abs(to.getTime() - from.getTime());
  const timeWindowInS = timeWindowInMS / 1000;
  const currentIntervalInS = Math.floor(timeWindowInS / activeAnalysesCount);

  const { emptySlotsWithAnalyses, emptySlotsWithCompliantAnalyses } = useActivationRangeDryRun(
    projectId,
    from,
    to,
    targetAnalysisIntervalInS === 0 ? null : targetAnalysisIntervalInS,
  );

  const compliantActivatableCount = emptySlotsWithCompliantAnalyses ?? 0;
  const activatableCount = emptySlotsWithAnalyses ?? 0;
  const nonCompliantActivatableCount = activatableCount - compliantActivatableCount;

  // desiredAnalysesCount is the targetAnalysesCount if my target interval could be translated directly to a number of analyses
  const desiredAnalysesCount =
    targetAnalysisIntervalInS === 0
      ? 0
      : Math.floor(timeWindowInS / targetAnalysisIntervalInS) + 1 - activeAnalysesCount;

  // The back sends us what it can create, so always use that number. If the math is wrong, it must be fixed
  // in the back.
  const targetAnalysesCount = bypassDataFilter ? activatableCount : compliantActivatableCount;

  const targetFrequencyTooSmall =
    currentIntervalInS <= targetAnalysisIntervalInS
      ? t("measures.chart.selected.analyses.modal.activate.errors.frequency_too_small")
      : timeWindowInS < targetAnalysisIntervalInS
        ? t(
            "measures.chart.selected.analyses.modal.activate.errors.frequency_too_small_for_selected_period",
          )
        : undefined;

  const frequencyTooSmallToCreateAnalyses =
    targetAnalysisIntervalInS > 0 && targetAnalysesCount === 0;

  const displayBypassCheckbox =
    (desiredAnalysesCount > targetAnalysesCount && activatableCount > targetAnalysesCount) ||
    bypassDataFilter;

  const handleSubmit = async () => {
    await activateRange({
      query: { from, to, intervalSeconds: targetAnalysisIntervalInS, bypass: bypassDataFilter },
      routeParams: { projectId },
    });

    addToast(
      "success",
      t("measures.chart.selected.analyses.select.range.success", targetAnalysesCount),
    );
    return true;
  };

  return (
    <ModalForm
      className={modalFormCss}
      cancellable
      onRequestClose={closeModal}
      onSubmit={
        targetAnalysesCount === 0 || targetAnalysisIntervalInS === 0 || targetFrequencyTooSmall
          ? false
          : handleSubmit
      }
      okButtonVariant="primary"
      okButtonName={t("measures.chart.selected.analyses.modal.activate.buttons.submit")}
    >
      <>
        <AnalysesSelectionSummary
          total={activeAnalysesCount}
          totalPossible={dataPointsCount}
          timeWindowInMS={timeWindowInMS}
        />

        <Hr />

        <Text className={marginBottomM}>
          {t("measures.chart.selected.analyses.modal.activate.information.indicate_interval")}
        </Text>
        <Text className={cx(targetFrequencyCss, marginBottomM)}>
          <>
            <span>
              {t("measures.chart.selected.analyses.modal.activate.labels.target_frequency.title")}
            </span>
            <Tooltip
              overlay={
                <OverlayWrapper
                  text={t(
                    "measures.chart.selected.analyses.modal.activate.labels.target_frequency.information",
                  )}
                />
              }
            >
              <span className={centerIcon}>
                <InformationLine color={colors.carbon800} />
              </span>
            </Tooltip>
          </>
        </Text>

        <DurationInput
          className={marginBottomM}
          error={targetFrequencyTooSmall}
          onChange={setTargetAnalysisIntervalInS}
        />

        {!targetFrequencyTooSmall &&
          (targetAnalysesCount !== 0 || nonCompliantActivatableCount > 0) && (
            <SelectionRow
              value={targetAnalysesCount}
              label={t("measures.chart.selected.analyses.modal.activate.labels.analyses_to_create")}
            />
          )}
        {!targetFrequencyTooSmall &&
          !displayBypassCheckbox &&
          frequencyTooSmallToCreateAnalyses &&
          emptySlotsWithAnalyses !== null && (
            <div className={cx(extraDataPoints, marginBottomM)}>
              <div className={informationIcon}>
                <InformationLine size="m" color={colors.darkNitrogen600} />
              </div>
              <span>
                {t(
                  "measures.chart.selected.analyses.modal.activate.errors.frequency_too_small_to_create_analyses",
                )}
              </span>
            </div>
          )}
        {!targetFrequencyTooSmall && displayBypassCheckbox && (
          <>
            <div className={cx(extraDataPoints, marginBottomM)}>
              <div className={informationIcon}>
                <InformationLine size="m" color={colors.darkNitrogen600} />
              </div>
              <span>
                {t(
                  "measures.chart.selected.analyses.modal.activate.form.extra_data_points.label",
                  nonCompliantActivatableCount,
                )}
              </span>
            </div>
            <Checkbox
              checked={bypassDataFilter}
              onChange={() => setBypassDataFilter(prevVal => !prevVal)}
            >
              <Text>
                {t(
                  "measures.chart.selected.analyses.modal.activate.form.extra_data_points.checkbox.label",
                  {
                    filtersLink: (
                      <LinkMemo
                        target="_blank"
                        className={checkboxLink}
                        to={toPath("/projects/:projectId/settings/filters", {
                          projectId,
                        })}
                      >
                        {t("measures.chart.selected.analyses.modal.checkbox.filters.link")}
                      </LinkMemo>
                    ),
                  },
                )}
              </Text>
            </Checkbox>
          </>
        )}
      </>
    </ModalForm>
  );
};

const targetFrequencyCss = css`
  font-size: ${fontSizes[4]};
  margin-bottom: ${spacings.xxs};
  color: ${colors.carbon800};
  display: flex;
  font-weight: 600;
`;

const modalFormCss = css`
  row-gap: 0;
`;

const checkboxLink = css`
  display: inline;
  color: ${colors.lightNitrogen500_Main};
  :hover {
    text-decoration: underline;
  }
`;
const centerIcon = css`
  padding-left: ${spacings.xxs};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const marginBottomM = css`
  margin-bottom: 16px;
`;

const extraDataPoints = css`
  display: flex;
  color: ${colors.darkNitrogen600};
`;
const informationIcon = css`
  margin-right: 10px;
`;

export default ActivateAnalyseModalForm;
